import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import Button, { ButtonGroup } from '../../library/button/button';
import Modal from '../../components/modal/modal';

import {
  useAddProductToCart,
  useFitment,
  useModal,
  useMyStore,
  useNavigateTo
} from '../../shared/hooks/hooks';
import useProductDetail from '../../shared/hooks/use-product-detail/use-product-detail';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { getStoreDataLayerValues } from '../../shared/utils/store/store';

import * as MODAL_TYPES from '../../shared/constants/modals';
import { BUNDLE_TYPE } from '../../shared/constants/products';

import './site-swap-modal.scss';

function SiteSwapModal(props) {
  const { onClose } = props;
  const { isFitmentLoading, selectedFitment } = useFitment();
  const { addProduct } = useAddProductToCart();
  const { confirmStore, myStore } = useMyStore();
  const { closeModal, openModal } = useModal();
  const history = useHistory();
  const location = useLocation();
  const navigateTo = useNavigateTo();

  const { getProductDetail } = useProductDetail({ myStore });

  function getStoreConfirmationData() {
    const params = queryString.parse(location.hash);
    let storeConfirmationData;

    if (params?.storeConfirmationData) {
      storeConfirmationData = JSON.parse(params.storeConfirmationData);
      delete params.storeConfirmationData;
      const hash =
        Object.keys(params).length > 0
          ? `#${queryString.stringify(params)}`
          : '';

      history.replace({
        hash,
        pathname: location.pathname,
        search: location.search,
        state: history.state
      });
    }

    return storeConfirmationData;
  }

  async function getProductItem() {
    const storeConfirmationData = getStoreConfirmationData();
    const packageCode =
      storeConfirmationData?.prebuiltPackage?.product1?.code &&
      storeConfirmationData?.prebuiltPackage?.product2?.code
        ? `${storeConfirmationData?.prebuiltPackage?.product1.code}-${storeConfirmationData?.prebuiltPackage?.product2.code}`
        : undefined;
    let productItem;

    if (storeConfirmationData) {
      await getProductDetail({
        fitment: selectedFitment,
        packageCode,
        productCode: storeConfirmationData?.productCode,
        rearProductCode: storeConfirmationData?.rearProductCode,
        store: myStore
      }).then(result => {
        const product = result?.product;

        if (product) {
          productItem = {
            prebuiltPackage: packageCode ? product : null,
            product: !packageCode ? product : null,
            quantity: storeConfirmationData.quantity,
            rearQuantity: storeConfirmationData.rearQuantity
          };
        }
      });
    }

    return productItem;
  }

  async function closeAndAddProduct(shouldConfirm = false) {
    const productItem = await getProductItem();

    if (productItem || shouldConfirm) {
      confirmStore();
    }

    if (productItem) {
      await addProduct({ isStoreConfirmed: true, productItem });
    }

    onClose();
  }

  return (
    <Modal
      heading={`Welcome to ${window.siteName}`}
      isOpen={!isFitmentLoading}
      onClose={closeAndAddProduct}
    >
      <p styleName="message">
        {`You're shopping in an area where our stores are called ${window.siteName}.`}
      </p>
      <ButtonGroup>
        <Button isPrimary onClick={() => closeAndAddProduct(true)}>
          Continue
        </Button>
        <Button
          onClick={async () => {
            const productItem = await getProductItem();

            if (productItem) {
              openModal(MODAL_TYPES.CHECK_INVENTORY, {
                cartReconciliationWarningMessage:
                  'Updating your store to this location results in the following changes:',
                headingMessage: 'Change Store',
                hideShorterWaitTimeMessage: true,
                isReadOnly: true,
                onClose,
                onSearch: searchedValue => {
                  trackAction('mystore_confirm_modal_changestore_search', {
                    store_search_term: searchedValue
                  });
                },
                onSetStore: async setStoreResult => {
                  const { bundleType, newProductData, storeData } =
                    setStoreResult || {};
                  const { code: storeCode } = storeData || {};
                  const isChangingStore = storeCode !== myStore?.code;

                  trackAction(
                    `mystore_confirm_modal_changestore_${
                      isChangingStore ? 'makethis' : 'continue'
                    }`,
                    isChangingStore
                      ? getStoreDataLayerValues(setStoreResult?.storeData)
                      : getStoreDataLayerValues(myStore)
                  );

                  if (newProductData) {
                    if (bundleType === BUNDLE_TYPE.TIRE_WHEEL_PACKAGE) {
                      productItem.prebuiltPackage = newProductData;
                    } else {
                      productItem.product = newProductData;
                    }
                  }

                  addProduct({
                    isStoreConfirmed: true,
                    productItem,
                    storeCode
                  });
                  closeModal();
                },
                productItem: productItem,
                selectStoreButtonLabel: 'Continue With This Store',
                showContinueWithMyStoreCTA: true
              });
            } else {
              onClose();
              navigateTo('/store-locator');
            }
          }}
        >
          Search in another area
        </Button>
      </ButtonGroup>
    </Modal>
  );
}

SiteSwapModal.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default SiteSwapModal;
